import type { AttemptDetails, AttemptInput } from '@dcs/contracts';
import type { RegulationId } from '@dcs/content';
import type { SimRuleCode } from '@dcs/content/sim-rules';
import type { ExamState, ScoreResult } from '@dcs/theory-core';

/**
 * Maps a finished exam to the body of `POST /api/attempts` (F6 T-2.2, contract `AttemptInput`):
 * theory from `scoreExam`'s result, sim from the exam store's `ExamResult`. Pure and type-only
 * imports, so the app shell carries it without the contract's zod schemas; the lazy sender
 * validates the body against the real schema before it leaves the device.
 *
 * `idempotencyKey` is not here: it is made once per finished exam (`attempt-stamp.ts`) and added by
 * the saver.
 */

export type AttemptDraft = Omit<AttemptInput, 'idempotencyKey'>;

/**
 * Version of the question bank the theory results were scored against, stamped on every theory
 * attempt (`QuestionBank.contentVersion`). Kept equal to `packages/content/data/questions/v1` by
 * a test: the bank itself is a lazy chunk and must not be imported to read one string.
 */
export const THEORY_CONTENT_VERSION = 'v1';

/** Same cap as the contract's `ATTEMPT_DETAILS_MAX_BYTES` and the DB CHECK (a test pins both). */
export const DETAILS_MAX_BYTES = 2048;
/** Same cap as the contract's id/code lists. */
export const DETAILS_MAX_LIST = 100;

const CODE = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/;
const isCode = (value: string): boolean => CODE.test(value);

const encoder = new TextEncoder();
const byteLength = (value: unknown): number => encoder.encode(JSON.stringify(value)).length;

type ListKey = 'wrongQuestionIds' | 'criticalWrongQuestionIds' | 'penaltyCodes';
const LIST_KEYS: readonly ListKey[] = [
  'wrongQuestionIds',
  'criticalWrongQuestionIds',
  'penaltyCodes',
];

/**
 * Makes `details` fit `AttemptInput.details`: drops values that are not codes, caps the lists at
 * 100, then, while the JSON is over 2 KB, drops `seed`, then `durationSec`, then removes the last
 * entry of the list that takes the most bytes (ties: `wrongQuestionIds`,
 * `criticalWrongQuestionIds`, `penaltyCodes`). Deterministic: the same input always gives the same output.
 */
export function fitDetails(details: AttemptDetails): AttemptDetails {
  const work: {
    -readonly [K in keyof AttemptDetails]: AttemptDetails[K];
  } = { ...details };
  for (const key of LIST_KEYS) {
    const list = work[key];
    if (list !== undefined) work[key] = list.filter(isCode).slice(0, DETAILS_MAX_LIST);
  }
  if (work.seed !== undefined && !isCode(work.seed)) delete work.seed;

  const over = (): boolean => byteLength(work) > DETAILS_MAX_BYTES;
  if (over()) delete work.seed;
  if (over()) delete work.durationSec;
  while (over()) {
    let heaviest: ListKey | null = null;
    let heaviestBytes = 0;
    for (const key of LIST_KEYS) {
      const list = work[key];
      const bytes = list === undefined || list.length === 0 ? 0 : byteLength(list);
      if (bytes > heaviestBytes) {
        heaviest = key;
        heaviestBytes = bytes;
      }
    }
    if (heaviest === null) break;
    work[heaviest] = (work[heaviest] ?? []).slice(0, -1);
  }
  return work;
}

export interface TheoryAttemptInput {
  readonly state: Pick<ExamState, 'exam' | 'startedAt' | 'submittedAt'>;
  readonly result: ScoreResult;
  /** Question bank version; default {@link THEORY_CONTENT_VERSION}. */
  readonly contentVersion?: string;
}

/** Theory mock exam: score = correct answers, maxScore = number of questions. */
export function theoryAttempt({ state, result, contentVersion }: TheoryAttemptInput): AttemptDraft {
  const { exam } = state;
  const finishedMs = state.submittedAt ?? state.startedAt;
  const details: AttemptDetails = {
    wrongQuestionIds: [...result.wrongIds],
    ...(result.criticalWrongIds.length > 0
      ? { criticalWrongQuestionIds: [...result.criticalWrongIds] }
      : {}),
    passScore: result.passScore,
    durationSec: Math.max(0, Math.round((finishedMs - state.startedAt) / 1000)),
    seed: exam.seed,
  };
  return {
    kind: 'theory',
    licenseClass: exam.licenseClass,
    regulationId: exam.regulationId,
    contentVersion: contentVersion ?? THEORY_CONTENT_VERSION,
    score: result.score,
    maxScore: result.total,
    passed: result.passed,
    failCode: result.passed ? null : (result.failCode ?? 'BELOW_PASS'),
    details: fitDetails(details),
    finishedAt: new Date(finishedMs).toISOString(),
  };
}

/** Failure code of a sim exam that ended with a low score and no elimination. */
export const SIM_BELOW_PASS = 'BELOW_PASS';

export interface SimAttemptInput {
  readonly result: {
    readonly passed: boolean;
    readonly score: number;
    /** The rule code that ended the exam early; `null` when the route was completed. */
    readonly reason: SimRuleCode | null;
  };
  readonly penalties: readonly { readonly code: SimRuleCode }[];
  readonly regulationId: RegulationId;
  readonly licenseClass: string;
  /** The rule table's `maxScore` and `passScore`. */
  readonly maxScore: number;
  readonly passScore: number;
  /** Version of the course the exam ran on (`course.meta.version`). */
  readonly contentVersion: string;
  readonly durationSec: number;
  /** ISO 8601, taken once when the exam ended. */
  readonly finishedAt: string;
}

/** Sim exam: `failCode` is the eliminating rule code, else `BELOW_PASS`. */
export function simAttempt(input: SimAttemptInput): AttemptDraft {
  const { result } = input;
  return {
    kind: 'sim',
    licenseClass: input.licenseClass,
    regulationId: input.regulationId,
    contentVersion: input.contentVersion,
    score: result.score,
    maxScore: input.maxScore,
    passed: result.passed,
    failCode: result.passed ? null : (result.reason ?? SIM_BELOW_PASS),
    details: fitDetails({
      penaltyCodes: input.penalties.map((p) => p.code),
      passScore: input.passScore,
      durationSec: Math.max(0, Math.round(input.durationSec)),
    }),
    finishedAt: input.finishedAt,
  };
}
